'use client';

import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef, isValidElement } from 'react';

/** 동적 `typo-${token}`은 Tailwind가 스캔하지 못해 CSS가 생성되지 않는다 */
const TYPO_CLASS = {
  h1: 'typo-h1',
  h2: 'typo-h2',
  h3: 'typo-h3',
  h4: 'typo-h4',
  h5: 'typo-h5',
  h6: 'typo-h6',
  h7: 'typo-h7',
  body1: 'typo-body1',
  body2: 'typo-body2',
  body3: 'typo-body3',
  body4: 'typo-body4',
  caption1: 'typo-caption1',
  caption2: 'typo-caption2',
} as const;

type TypoToken = keyof typeof TYPO_CLASS;

export interface SkeletonProps extends ComponentPropsWithoutRef<'span'> {
  /** true면 skeleton UI 표시, false면 children 표시 */
  loading?: boolean;
  /**
   * 자리를 대신할 텍스트의 타이포 토큰. 높이를 그 line-height에 정확히 맞춰
   * 로딩 → 실제 텍스트 전환의 레이아웃 시프트를 없앤다.
   * `h-*`를 눈대중으로 고르면 반올림 오차만큼 시프트가 남는다 (typo-body4는 21px).
   */
  typo?: TypoToken;
}

export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(
  ({ children, className, loading = true, typo, ...props }, ref) => {
    if (!loading) return children;

    const Comp = isValidElement(children) ? Slot : 'span';

    return (
      <Comp
        ref={ref}
        aria-hidden
        className={cn(
          'block animate-pulse bg-Gray-black/10 motion-reduce:animate-none',
          typo && TYPO_CLASS[typo],
          className,
        )}
        tabIndex={-1}
        {...props}
      >
        {children ?? (typo ? '\u00A0' : null)}
      </Comp>
    );
  },
);

Skeleton.displayName = 'Skeleton';
